/**
 * i18n per 01 §13: templates come from the gettext catalogue (JSON, translated server-side by TranslatePress);
 * this module only fills numbered placeholders (%1$s, %2$d…) and formats values with Intl in the page language.
 * Values are inserted as <span data-no-translation> so TranslatePress never touches them.
 *
 * Whitespace around a value is a separate token, also rendered in a <span data-no-translation>: on translated
 * pages TranslatePress dynamic translation (trp-translate-dom-changes.js) rewrites every text node our JS inserts
 * and trims it, which turned "4 personnes : 2 chambres" into "4personnes :2chambres" (verified on /en/ and /de/,
 * 19 Oct 2026, by blocking that script). Whitespace never lives at the edge of a translatable text node.
 * Never build a sentence by concatenation: every visible sentence is a catalogue template.
 *
 * Static labels (no placeholder) come from the page HTML (CHANGE 2026-10-18 §5): the shortcode prints them in
 * French in a hidden .pap-labels list, as page content that TranslatePress translates server-side; templates
 * with placeholders come from the JSON catalogue. A label found in the page wins over the catalogue.
 */
import { track } from './analytics.js';

/** One `erreur INVALID_DATE` per page (date()). */
let invalidDateSent = false;

const PLACEHOLDER = /%(\d+)\$[sd]/g;

/**
 * Splits a template into text, value and space tokens (pure; tested with node --test). The whitespace that
 * touches a value is moved out of the text into its own 'space' token (kept safe from TranslatePress).
 * @param {string} template e.g. "Vous êtes %1$d. Choisissez au moins %2$d chambres…"
 * @param {Array<string|number>} values
 * @returns {Array<{type: 'text'|'space', text: string}|{type: 'value', index: number, text: string}>}
 */
export function tokens(template, values = []) {
	const out = [];
	const raw = rawTokens(template, values);
	raw.forEach((tok, i) => {
		if (tok.type !== 'text') {
			out.push(tok);
			return;
		}
		const lead = i > 0 && raw[i - 1].type === 'value' ? tok.text.match(/^\s+/)?.[0] ?? '' : '';
		const rest = tok.text.slice(lead.length);
		const trail = i < raw.length - 1 && raw[i + 1].type === 'value' ? rest.match(/\s+$/)?.[0] ?? '' : '';
		const middle = rest.slice(0, rest.length - trail.length);
		if (lead) {
			out.push({ type: 'space', text: lead });
		}
		if (middle) {
			out.push({ type: 'text', text: middle });
		}
		if (trail) {
			out.push({ type: 'space', text: trail });
		}
	});
	return out;
}

function rawTokens(template, values) {
	const out = [];
	let last = 0;
	for (const m of String(template).matchAll(PLACEHOLDER)) {
		if (m.index > last) {
			out.push({ type: 'text', text: template.slice(last, m.index) });
		}
		const index = Number(m[1]) - 1;
		out.push({ type: 'value', index, text: values[index] === undefined ? m[0] : String(values[index]) });
		last = m.index + m[0].length;
	}
	if (last < template.length) {
		out.push({ type: 'text', text: template.slice(last) });
	}
	return out;
}

/**
 * Template for an entry: plain string, or {one, other} picked with Intl.PluralRules (pure).
 * @param {string|{one: string, other: string}} entry
 * @param {number|null} count
 * @param {string} lang
 * @returns {string}
 */
export function pick(entry, count, lang) {
	if (entry === undefined || entry === null) {
		return '';
	}
	if (typeof entry === 'string') {
		return entry;
	}
	const category = new Intl.PluralRules(lang).select(Number(count ?? 0));
	return category === 'one' ? entry.one : entry.other;
}

/**
 * Labels printed by the shortcode in every block: <ul hidden class="pap-labels"><li data-k="ui.search">Rechercher</li>,
 * plural forms with data-n. The first value found for a key wins (every block carries the same list).
 * @param {ParentNode} root
 * @returns {Object<string, string|{one: string, other: string}>}
 */
export function readLabels(root = document) {
	const out = {};
	for (const el of root.querySelectorAll('.pap-labels [data-k]')) {
		const key = el.dataset.k;
		if (el.dataset.n) {
			if (typeof out[key] === 'object' && out[key][el.dataset.n] !== undefined) {
				continue;
			}
			out[key] = { ...(typeof out[key] === 'object' ? out[key] : {}), [el.dataset.n]: el.textContent };
		} else {
			out[key] ??= el.textContent;
		}
	}
	return out;
}

/**
 * Creates the translator for one page.
 * @param {Object<string, string|{one: string, other: string}>} templates JSON catalogue (templates; may hold labels too)
 * @param {string} lang Page language (BCP-47, from <html lang>).
 * @param {Object<string, string|{one: string, other: string}>} [labels] Labels from the page (win).
 * @param {Object<string, string|{one: string, other: string}>} [fallback] French labels from the payload: used
 *   only for a label missing from the page — a key is never shown; one console warning lists the missing keys.
 */
export function createI18n(templates, lang, labels = {}, fallback = {}, timeZone = undefined) {
	// The site's time zone (payload `timeZone`, 1.2.3): dates and clock times are formatted in it, never in a built-in one.
	const zone = () => {
		if (!timeZone) {
			throw new Error('createI18n: no time zone');
		}
		return timeZone;
	};
	const missing = Object.keys(fallback).filter((key) => !(key in labels) && !(key in templates));
	if (missing.length) {
		console.warn(`[papounan] ${missing.length} label(s) missing from the page, French used instead: ${missing.join(', ')}`);
	}
	const catalogue = { ...fallback, ...templates, ...labels };
	// The property's currency (Phase 3: /start config.property.currency, set by useCurrency() — none built in); always
	// 2 decimals: « 465,00 € » (Money is in cents, N2).
	let money = null;
	const useCurrency = (code) => {
		money = new Intl.NumberFormat(lang, { style: 'currency', currency: code, minimumFractionDigits: 2, maximumFractionDigits: 2 });
	};
	const formatMoney = (amount) => {
		if (!money) {
			throw new Error('i18n.money: no currency (config.property.currency)');
		}
		// The sign as a true minus (U+2212, 1.4.6), like the labels' « −20 % »: Intl gives a hyphen-minus. Formatting only.
		return money.formatToParts(Number(amount)).map((p) => (p.type === 'minusSign' ? '\u2212' : p.value)).join('');
	};
	const percent = new Intl.NumberFormat(lang, { style: 'percent', maximumFractionDigits: 2 });
	const formats = {
		day: { weekday: 'long', day: 'numeric', month: 'long' },
		short: { day: 'numeric', month: 'long' },
		dayMonth: { day: 'numeric', month: 'short' }, // 20 oct. (dates summary)
		long: { day: 'numeric', month: 'long', year: 'numeric' },
		month: { month: 'long', year: 'numeric' },
		weekday: { weekday: 'short' },
		dayNumber: { day: 'numeric' },
		aria: { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' },
		stay: { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' } // mer. 19 mai 2027 (« Votre séjour », 0.10.0)
	};
	const dateFormatters = {};

	/** A formatted value, protected from translation. */
	function value(text) {
		const span = document.createElement('span');
		span.className = 'pap-value';
		span.setAttribute('data-no-translation', '');
		span.textContent = text;
		return span;
	}

	/**
	 * Template as a DOM fragment (text nodes + value spans). A value may be a DOM node (e.g. a tel:/mailto:
	 * link): it is inserted as-is and must carry data-no-translation itself (see link()).
	 * @param {string} key Catalogue key.
	 * @param {Array<string|number|Node>} [values]
	 * @param {number|null} [count] Plural count (defaults to the first value).
	 */
	function t(key, values = [], count = null) {
		const frag = document.createDocumentFragment();
		const template = pick(catalogue[key], count ?? values[0], lang) || key;
		for (const tok of tokens(template, values.map((v) => (v instanceof Node ? '' : v)))) {
			if (tok.type === 'text') {
				frag.appendChild(document.createTextNode(tok.text));
			} else if (tok.type === 'space') {
				frag.appendChild(sep(tok.text));
			} else {
				const v = values[tok.index];
				frag.appendChild(v instanceof Node ? v.cloneNode(true) : value(tok.text));
			}
		}
		return frag;
	}

	/**
	 * Separator or whitespace between two fragments (« · », « — », a space), protected from translation: a plain
	 * text node there would be trimmed by TranslatePress on translated pages.
	 */
	function sep(text) {
		const span = document.createElement('span');
		span.className = 'pap-sp';
		span.setAttribute('data-no-translation', '');
		span.textContent = text;
		return span;
	}

	/** A link value (tel:, mailto:) protected from translation. */
	function link(href, text) {
		const a = document.createElement('a');
		a.href = href;
		a.className = 'pap-value';
		a.setAttribute('data-no-translation', '');
		a.textContent = text;
		return a;
	}

	/** Template as a plain string (attributes such as aria-label). */
	function s(key, values = [], count = null) {
		return tokens(pick(catalogue[key], count ?? values[0], lang) || key, values).map((tok) => tok.text).join('');
	}

	/**
	 * Formats a YYYY-MM-DD date (noon UTC, Paris time zone). Not a date (null, invalid): '' and one Matomo `erreur`
	 * INVALID_DATE per page — never throws (1.6.1: a missing hold date stopped the cheque booking).
	 */
	function date(iso, style = 'short') {
		const at = typeof iso === 'string' ? new Date(iso + 'T12:00:00Z') : null;
		if (!at || Number.isNaN(at.getTime())) {
			if (!invalidDateSent) {
				invalidDateSent = true;
				track('erreur', 'INVALID_DATE');
			}
			return '';
		}
		dateFormatters[style] ??= new Intl.DateTimeFormat(lang, { ...formats[style], timeZone: zone() });
		return dateFormatters[style].format(at);
	}

	/**
	 * Two dates as one short range, by Intl (search bar's phone tab, 0.9.9): « 17–25 mars », « 29 mars – 5 avr. »;
	 * « 17 mars – 25 mars » where formatRange is missing.
	 */
	function dateRange(from, to) {
		dateFormatters.dayMonth ??= new Intl.DateTimeFormat(lang, { ...formats.dayMonth, timeZone: zone() });
		const f = dateFormatters.dayMonth;
		const a = new Date(from + 'T12:00:00Z');
		const b = new Date(to + 'T12:00:00Z');
		return typeof f.formatRange === 'function' ? f.formatRange(a, b) : f.format(a) + ' – ' + f.format(b);
	}

	/**
	 * Times of day (0.10.0: check-in window, arrival slots, the card booking's hold), by Intl in the page language:
	 * « 17:00 » in French, « 5:00 PM » in English. hhmm = "HH:MM" from the server (wall-clock time, no time zone).
	 */
	let timeFormatter = null;
	const wallClock = (hhmm) => {
		const [hour, minute] = String(hhmm).split(':').map(Number);
		return new Date(Date.UTC(2000, 0, 1, hour || 0, minute || 0));
	};
	function timeFmt() {
		timeFormatter ??= new Intl.DateTimeFormat(lang, { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' });
		return timeFormatter;
	}
	/** « 17:00 ». */
	function time(hhmm) {
		return timeFmt().format(wallClock(hhmm));
	}
	/** « 17:00 – 20:30 » (Intl formatRange; « 5:00 – 8:30 PM »). */
	function timeRange(from, to) {
		const f = timeFmt();
		return typeof f.formatRange === 'function' ? f.formatRange(wallClock(from), wallClock(to)) : f.format(wallClock(from)) + ' – ' + f.format(wallClock(to));
	}
	/** Time of an ISO date-time in Paris (« 15:42 »): the hold of an unpaid card booking. */
	function clock(iso) {
		dateFormatters.clock ??= new Intl.DateTimeFormat(lang, { hour: 'numeric', minute: '2-digit', timeZone: zone() });
		return dateFormatters.clock.format(new Date(iso));
	}

	/**
	 * The sub-line under « Code X » (1.4.8): template `promo.label.{code}` of the server's label (Domain\Search\PromoLabel,
	 * the one mapping of Beds24's voucher type), filled with its percent or amount — formatted, never computed. Null
	 * without a label or a template. Here so the booking blocks and the lazy checkout / « merci » share it.
	 */
	function promoLabel(label, family = 'label') {
		const key = 'promo.' + family + '.' + label?.code;
		if (!label?.code || !(key in catalogue)) {
			return null;
		}
		if (typeof label.percent === 'number') {
			return t(key, [percentOf(label.percent)]);
		}
		return typeof label.amount === 'number' ? t(key, [formatMoney(label.amount)]) : null;
	}
	const percentOf = (p) => percent.format(Number(p) / 100);

	return {
		lang,
		t,
		s,
		value,
		sep,
		link,
		has: (key) => key in catalogue,
		money: formatMoney,
		useCurrency,
		percent: percentOf,
		promoLabel,
		promoBanner: (label) => promoLabel(label, 'banner'), // the results note (1.4.12): what the code is
		date,
		dateRange,
		time,
		timeRange,
		clock,
		zone
	};
}
